<script setup lang="ts">
import { computed, inject, onMounted, onUnmounted, ref, watch } from 'vue';
import type { ScItem } from '@/types/sitecore';
import {
  FormCheckListEmits,
  FormCheckListItemFields,
  FormCheckListProps,
  FormCheckListRef,
} from '@/types/form/form-check-list';
import type { FormElMsgData } from '@/types/form/others/form-el-msg';
import type { DynamicFormContext, DynamicFormFieldValidateResult } from '@/types/form/dynamic-form';
import utils from '@/utils';
import FormElMsg from '@/form/other/FormElMsg.vue';
import SiteRichText from '@/elements/SiteRichText.vue';
import Checkbox from '@/elements/Checkbox.vue';
import { transitions } from '@/utils/transitions';

const props = defineProps<FormCheckListProps>();
const emit = defineEmits<FormCheckListEmits>();
const formContext = inject<DynamicFormContext>('formContext')!;
const fields = computed(() => props.item.fields);
const rootEl = ref<HTMLElement>();

/* ---------- state ---------- */
const visibleValues = ref<string[]>([]);
const formCheckedItems = computed(
  () => fields.value?.items?.filter((x) => x.fields?.checked.value) ?? []
);
const checkedItems = ref<ScItem<FormCheckListItemFields>[]>(formCheckedItems.value);
const msg = ref<FormElMsgData | undefined>(undefined);
const isRequired = ref(fields.value?.required.value ?? false);
const isRadio = ref(fields.value?.radio?.value ?? false);
const isDisabled = ref(false);
const isVisible = ref(true);
const showItems = ref<ScItem<FormCheckListItemFields>[]>(fields.value?.items ?? []);
const expanded = ref<boolean>(
  fields.value?.collapsible?.value ? (fields.value?.expanded?.value ?? false) : true
);

/* ---------- computed ---------- */
const rootClass = computed(() => [
  'f-check-list',
  {
    required: isRequired.value,
    disabled: isDisabled.value,
    radio: isRadio.value,
    collapsible: fields.value?.collapsible.value,
    expanded: expanded.value,
  },
]);

const isItemChecked = (item: ScItem<FormCheckListItemFields>) => {
  return checkedItems.value.some((x) => x.fields?.data.value === item.fields?.data.value);
};

/* ---------- methods ---------- */
const isAllOption = (item: ScItem<FormCheckListItemFields> | undefined) => {
  const text = item?.fields?.mandatoryText?.value || '';
  return /^all\b/i.test(text);
};

const toggleItemChecked = (clickedItem: ScItem<FormCheckListItemFields>) => {
  if (isDisabled.value || !clickedItem.fields) return;
  msg.value = undefined;
  let value: ScItem<FormCheckListItemFields>[];
  if (isRadio.value) {
    const checked = checkedItems.value.some((x) => x.id === clickedItem.id);
    if (checked) {
      value = [];
    } else {
      value = [clickedItem];
    }
  } else {
    const allItems = fields.value?.items ?? [];
    const allItem = allItems.find((x) => isAllOption(x));

    if (allItem && clickedItem.id === allItem.id) {
      // "All" option clicked: toggle all items
      const isAllChecked = checkedItems.value.some((x) => x.id === allItem.id);
      value = isAllChecked ? [] : [...allItems];
    } else {
      // Regular item: toggle it
      value = [...checkedItems.value];
      const index = value.findIndex((x) => x.id === clickedItem.id);
      if (index >= 0) value.splice(index, 1);
      else value.push(clickedItem);

      // Sync "All" option state
      if (allItem) {
        const subItems = allItems.filter((x) => !isAllOption(x));
        const allSubSelected = subItems.every((x) => value.some((v) => v.id === x.id));
        const allIndex = value.findIndex((x) => x.id === allItem.id);
        if (allSubSelected && allIndex < 0) {
          value.push(allItem);
        } else if (!allSubSelected && allIndex >= 0) {
          value.splice(allIndex, 1);
        }
      }
    }
  }
  emit(
    'change',
    'form-check-list',
    fields.value?.fieldName.value ?? '',
    value.map((i) => i.fields?.data.value || ''),
    value
  );
  checkedItems.value = value;
};

const toggleExpanded = () => {
  expanded.value = !expanded.value;
};
watch(
  () => formCheckedItems.value,
  () => {
    checkedItems.value = formCheckedItems.value;
  },
  {
    immediate: true,
  }
);
/* ---------- watchers ---------- */
watch(
  () => [fields.value, visibleValues.value],
  () => {
    if (
      !utils.obj.isNoE(visibleValues.value) &&
      !utils.obj.isNoE(fields.value) &&
      !utils.obj.isNoE(fields.value?.items)
    ) {
      const items =
        visibleValues.value.length > 0
          ? fields.value!.items.filter((x) =>
              visibleValues.value.some((y) => utils.string.eq(x.fields!.data.value, y))
            )
          : fields.value!.items;
      if (!utils.obj.isNoE(items)) {
        showItems.value = items!;
      }
    }
  },
  { immediate: true }
);

watch(
  () => [checkedItems.value, fields.value, visibleValues.value],
  () => {
    const itemsList =
      visibleValues.value.length > 0
        ? fields.value!.items.filter((x) =>
            visibleValues.value.some((y) => utils.string.eq(x.fields!.data.value, y))
          )
        : fields.value!.items;

    showItems.value = itemsList.map((i) => {
      if (i.fields?.checked && i.fields?.disabled) {
        if (
          checkedItems.value.some((c) =>
            utils.string.eq(i.fields?.data?.value, c.fields?.data?.value)
          ) &&
          i.fields?.checked
        ) {
          i.fields.checked.value = true;
        } else {
          i.fields.checked.value = false;
          i.fields.disabled.value = !!(
            fields.value?.maxChoice?.value &&
            fields.value.maxChoice.value > 0 &&
            checkedItems.value.length >= fields.value.maxChoice.value &&
            i.fields?.disabled
          );
        }
      }
      return i;
    });
  },
  { immediate: true, deep: true }
);

watch(
  () => fields.value?.radio?.value,
  (newVal) => {
    isRadio.value = newVal ?? false;
  }
);

/* ---------- expose ---------- */
const getRefs = (): FormCheckListRef => {
  return {
    el: rootEl.value || null,
    id: props.item.id,
    name: fields.value?.fieldName.value,
    type: 'form-check-list',
    visible: isVisible.value,
    setRequired(required: boolean) {
      isRequired.value = required;
    },
    setDisabled(disabled: boolean) {
      isDisabled.value = disabled;
    },
    setVisible(visible: boolean) {
      isVisible.value = visible;
    },
    updateVisibleValues(values: string[]) {
      if (!fields.value?.items) return;
      visibleValues.value = values;
    },
    updateValue(value?: string | string[]) {
      if (!fields.value?.items || !value) {
        checkedItems.value = [];
        return;
      }
      let checkedValue: string[] = [];
      if (Array.isArray(value)) {
        checkedValue = value;
      } else {
        checkedValue = value.split(';');
      }

      checkedItems.value = fields.value?.items.filter((x) =>
        checkedValue.includes(x.fields!.data.value)
      );
    },
    async validate() {
      const result: DynamicFormFieldValidateResult = {
        value:
          checkedItems.value.length > 0
            ? checkedItems.value.map((x) => x.fields?.data.value).join(';')
            : undefined,
        valid: true,
      };
      if (!fields.value) {
        result.valid = false;
        return result;
      }
      if (isRequired.value && checkedItems.value.length === 0) {
        const _msg: FormElMsgData = {
          type: 'error',
          message: utils.obj.isNoE(fields.value?.requiredMsg?.value)
            ? 'This field is required'
            : fields.value.requiredMsg.value,
        };
        result.valid = false;
        result.message = _msg.message;
        msg.value = _msg;
        return result;
      }
      // if (fields.value?.maxChoice?.value && fields.value.maxChoice.value > 0 && checkedItems.value.length > fields.value.maxChoice.value) {
      //   const _msg: FormElMsgData = {
      //     type: 'error',
      //     message: utils.obj.isNoE(fields.value?.maxChoiceError?.value) ? '' : fields.value.maxChoiceError.value.replace('##count##', `${fields.value.maxChoice.value}`)
      //   };
      //   result.valid = false;
      //   result.message = _msg.message;
      //   msg.value = _msg;
      //   return result;
      // }
      return result;
    },
  } as FormCheckListRef;
};
onMounted(() => {
  formContext.regFormEl(getRefs());
});

onUnmounted(() => {
  formContext.unRegFormEl(getRefs());
});
defineExpose(getRefs());
</script>
<template>
  <div v-if="fields && isVisible" :class="rootClass" ref="rootEl">
    <div
      class="f-check-list__header"
      v-if="fields.collapsible?.value || !utils.obj.isNoE(fields.label?.value)"
      @click="toggleExpanded"
    >
      <div class="f-check-list__header-left">
        <div
          class="f-check-list__placeholder"
          :class="[
            'f-check-list__placeholder',
            { 'as-label': fields.collapsible?.value && !expanded && checkedItems.length > 0 },
          ]"
        >
          <JssRichText class="f-check-list__label" :field="fields.label" tag="span" />
          <span class="f-input__required" v-if="isRequired">*</span>
        </div>
        <Transition
          @before-enter="transitions.accordion.beforeEnter"
          @enter="transitions.accordion.enter"
          @leave="transitions.accordion.leave"
        >
          <div class="f-check-list__value" v-if="!expanded">
            {{ checkedItems.map((x) => x.fields?.mandatoryText.value).join(' ') }}
          </div>
        </Transition>
      </div>
      <icon class="f-check-list__expander" name="down" v-if="fields.collapsible?.value" />
    </div>
    <Transition
      @before-enter="transitions.accordion.beforeEnter"
      @enter="transitions.accordion.enter"
      @leave="transitions.accordion.leave"
    >
      <div v-show="expanded">
        <div class="f-check-list__main">
          <site-rich-text :field="fields.description" class="f-check-list__des" />

          <div :class="['f-check-list__items', { isRadio }]">
            <div
              v-for="(showItem, index) in showItems"
              :key="index"
              :class="[
                'f-check-list__item',
                {
                  checked: isItemChecked(showItem),
                  disabled: showItem.fields?.disabled?.value,
                },
              ]"
            >
              <checkbox
                :is-radio="isRadio"
                :value="isItemChecked(showItem)"
                @click="toggleItemChecked(showItem)"
              />
              <div class="f-check-list__item-label" @click="toggleItemChecked(showItem)">
                {{ showItem.fields?.mandatoryText?.value }}
                <span
                  v-if="showItem.fields?.tooltip?.value"
                  :title="showItem.fields.tooltip.value"
                  class="e-site-icon size-xs use-px"
                  >?</span
                >
              </div>
            </div>
          </div>
          <form-el-msg :msg="msg" />
        </div>
      </div>
    </Transition>
  </div>
</template>

<style lang="scss">
@import '../styles/variables';
@import '../styles/functions';
@import '../styles/mixins';

.f-check-list {
  $c: &;
  border: 1px solid transparent;
  border-radius: 8px;
  &__header {
    min-height: 60px;
    display: flex;
    justify-content: space-between;
    gap: 16px;
    &-left {
      position: relative;
      flex: 1;
      align-self: stretch;
      display: flex;
      align-items: flex-end;
    }
  }
  &__expander {
    flex-shrink: 0;
    align-self: center;
    cursor: pointer;
  }
  &__placeholder {
    position: absolute;
    max-width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    pointer-events: none;
    display: flex;
    align-items: center;
    gap: 4px;
    color: var(--item-color, #929292);
    font-size: 16px;
    padding: 10px 0;
    transition: all 0.2s linear;
    &.as-label {
      font-size: 12px;
      height: 36px;
      opacity: 0.7;
    }
  }
  &__value {
    flex-grow: 1;
    font-size: 14px;
    cursor: pointer;
  }

  &__label {
    flex-grow: 1;
    color: var(--color, currentColor);
    .e-site-rich-text {
      display: inline-block;
    }
  }

  &__des {
    display: inline-block;
    margin-bottom: 16px;
  }

  &__main {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  &__items {
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding-top: 16px;
  }

  &__item {
    display: flex;
    gap: 16px;
    align-items: center;

    &-check {
      flex-shrink: 0;
      width: 16px;
      height: 16px;
      border: 1px solid $white;
      cursor: pointer;
    }

    &-label {
      line-height: 18px;
      cursor: pointer;
    }

    &.checked {
      &-check {
        background-color: $color-primary-blue;
        color: $white;
      }
    }
    &:hover {
      .e-checkbox {
        &:not(.checked) {
          background-color: rgba(from var(--checkbox-back-color, #6b9171) r g b / 0.1);
        }
      }
    }
    &.disabled {
      cursor: not-allowed;
      pointer-events: none;
      opacity: 0.6;

      #{$c}__item {
        &-check {
          cursor: not-allowed;
          pointer-events: none;
        }

        &-label {
          cursor: not-allowed;
          pointer-events: none;
        }
      }

      &:hover {
        cursor: not-allowed;
      }
    }
  }

  &.disabled {
    #{$c}__item {
      &-check {
        background-color: rgba($grey, 0.3);
        color: $grey;
      }
    }
  }

  &.collapsible {
    border-color: var(--item-border-color, #c4c4c4);
    background-color: var(--item-back-color, #fff);
    color: var(--item-color, #000);
    padding: 8px 16px;
  }
  &.expanded {
    #{$c}__expander {
      transform: rotate(180deg);
    }
  }
  @include tablet-landscape {
    &__items {
      flex-direction: row;
      flex-wrap: wrap;
    }
    &__item {
      flex: 0 0 calc(50% - 8px);
    }
    &__des {
      margin-bottom: 24px;
    }
  }
}

/* Vue Transition for accordion */
.accordion-enter-active,
.accordion-leave-active {
  transition: all 0.3s ease;
  overflow: hidden;
}

.accordion-enter-from,
.accordion-leave-to {
  height: 0;
  opacity: 0;
  transform: scale(0.95);
}

.accordion-enter-to,
.accordion-leave-from {
  height: auto;
  opacity: 1;
  transform: scale(1);
}
</style>
